// Design tokens for the monitor ("glass" direction). Raw values live here so
// charts (Recharts props) and the MUI theme read from one source.

export const tokens = {
  // Canvas stays brand black; depth comes from the ambient layer + glass.
  canvas: "#000000",
  sidebar: "rgba(20, 20, 22, 0.55)",
  card: "#121214",
  raised: "rgba(0, 0, 0, 0.28)",
  overlay: "rgba(24, 24, 27, 0.92)",
  hover: "rgba(255, 255, 255, 0.05)",

  // Hairlines
  borderSubtle: "rgba(255, 255, 255, 0.06)",
  border: "rgba(255, 255, 255, 0.10)",
  borderStrong: "rgba(255, 255, 255, 0.18)",

  // Text
  textPrimary: "#FFFFFF",
  textSecondary: "rgba(255, 255, 255, 0.64)",
  textMuted: "rgba(255, 255, 255, 0.5)",

  // Brand
  gold: "#CDA075",
  goldLight: "#F1D3AF",
  goldDark: "#A97E55",
  green: "#408856",
  greenLight: "#55A06D",
  greenDark: "#2F6B43",

  // Semantic (reserved for state, never for series identity)
  positive: "#62BE80",
  negative: "#FF7070",
  warning: "#FFB547",
  info: "#6CB8FF",

  // Charts — categorical slots validated for CVD separation on the dark surface.
  chart: {
    gold: "#BC8A4C",
    green: "#4C8FE0",
    violet: "#D06C9C",
    lp: "#F1D3AF",
    hodl: "rgba(255, 255, 255, 0.38)",
    grid: "rgba(255, 255, 255, 0.07)",
    axis: "rgba(255, 255, 255, 0.40)",
  },

  // Chain accents (card glows, avatars)
  chainTint: {
    ETH: "#7B8CFF",
    ARB: "#3BA3F5",
    RSK: "#FF9A2E",
  } as Record<string, string>,

  // Glass recipe
  glass: {
    bg: "linear-gradient(180deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.025) 100%)",
    bgStrong: "linear-gradient(180deg, rgba(255,255,255,0.11) 0%, rgba(255,255,255,0.04) 100%)",
    chrome: "rgba(16, 16, 18, 0.62)",
    border: "rgba(255, 255, 255, 0.10)",
    highlight: "inset 0 1px 0 rgba(255, 255, 255, 0.10)",
    shadow: "0 30px 60px -30px rgba(0, 0, 0, 0.9)",
    blur: "blur(22px) saturate(140%)",
    inset: "rgba(0, 0, 0, 0.28)",
  },

  goldGradient: "linear-gradient(180deg, #E5BF95 0%, #C99868 100%)",
  goldGlow: "0 10px 30px -10px rgba(205, 160, 117, 0.75)",

  radius: {
    sm: 12,
    md: 16,
    lg: 26,
    xl: 32,
    pill: 999,
  },
} as const;

/** Glass surface styles (background, hairline, highlight, optional blur). */
export const glassSurface = (opts: { strong?: boolean; blur?: boolean } = {}) => ({
  background: opts.strong ? tokens.glass.bgStrong : tokens.glass.bg,
  border: `1px solid ${tokens.glass.border}`,
  boxShadow: `${tokens.glass.highlight}, ${tokens.glass.shadow}`,
  ...(opts.blur !== false
    ? { backdropFilter: tokens.glass.blur, WebkitBackdropFilter: tokens.glass.blur }
    : {}),
});

// Kept for components that still draw a masked hairline edge.
export const gradientBorder = (radius: number | string, from = 0.16, to = 0.03) => ({
  content: '""',
  position: "absolute" as const,
  inset: 0,
  borderRadius: radius,
  padding: "1px",
  background: `linear-gradient(180deg, rgba(255,255,255,${from}) 0%, rgba(255,255,255,${to}) 55%, rgba(255,255,255,${to}) 100%)`,
  WebkitMask:
    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
  WebkitMaskComposite: "xor",
  maskComposite: "exclude",
  pointerEvents: "none" as const,
});
